import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { socialLinks, studioInfo } from '../data/mock';
import { MessageCircle, MapPin, Clock, Phone } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

const Contact = () => {
    const { t, isRTL } = useLanguage();
    const beitShemeshPosition = [31.748855524475157, 34.986464324004615];
    const ganYavnePosition = [31.79522861760972, 34.70445779647841]; // Example coordinates for Gan Yavne branch
    const customIcon = new L.Icon({
        iconUrl: 'logo.png', // public/marker.png
        iconSize: [40, 40],
        iconAnchor: [20, 40],
        popupAnchor: [0, -40],
    });

    return (
        <section
            id='contact'
            className='relative bg-black py-24 overflow-hidden'
        >
            {/* Decorative Background */}
            <div className='absolute inset-0 opacity-30'>
                <div className='top-0 left-1/4 absolute bg-mid-green/20 blur-3xl rounded-full w-96 h-96' />
                <div className='right-1/4 bottom-0 absolute bg-mid-purple/20 blur-3xl rounded-full w-80 h-80' />
            </div>

            <div className='z-10 relative mx-auto px-4 lg:px-8 max-w-5xl'>
                {/* Header */}
                <div className='mb-16 text-center'>
                    <span className='text-mid-green text-sm uppercase tracking-[0.2em]'>
                        {t.contact.subtitle}
                    </span>
                    <h2 className='mt-4 font-display text-white text-4xl lg:text-6xl uppercase'>
                        {t.contact.title}
                    </h2>
                </div>

                {/* Contact Card */}
                <div className='bg-white/5 backdrop-blur-sm p-8 lg:p-12 border border-white/10 rounded-2xl'>
                    <div className='items-center gap-12 grid lg:grid-cols-2'>
                        {/* Info Side */}
                        <div className='space-y-8'>
                            <div className='flex items-start gap-4'>
                                <div className='bg-mid-purple/20 p-3 rounded-lg'>
                                    <MapPin className='w-6 h-6 text-mid-purple' />
                                </div>
                                <div>
                                    <h3 className='mb-1 font-medium text-white'>
                                        {isRTL
                                            ? 'סניף בית שמש'
                                            : 'Beit Shemesh Branch'}
                                    </h3>
                                    <p className='text-mid-grey'>
                                        {t.contact.address}
                                    </p>
                                </div>
                            </div>
                            <div className='flex items-start gap-4'>
                                <div className='bg-mid-purple/20 p-3 rounded-lg'>
                                    <MapPin className='w-6 h-6 text-mid-purple' />
                                </div>
                                <div>
                                    <h3 className='mb-1 font-medium text-white'>
                                        {isRTL
                                            ? 'סניף גן יבנה'
                                            : 'Gan Yavne Branch'}
                                    </h3>
                                    <p className='text-mid-grey'>
                                        {isRTL
                                            ? 'רחוב המגינים 56 קניון פרנדלי גן יבנה'
                                            : '56 HaMeginim St, Friendly Mall, Gan Yavne'}
                                    </p>
                                </div>
                            </div>

                            <div className='flex items-start gap-4'>
                                <div className='bg-light-pink/20 p-3 rounded-lg'>
                                    <Phone className='w-6 h-6 text-light-pink' />
                                </div>
                                <div>
                                    <h3 className='mb-1 font-medium text-white'>
                                        {isRTL ? 'טלפון' : 'Phone'}
                                    </h3>
                                    <p className='text-mid-grey'>
                                        {studioInfo.phone}
                                    </p>
                                </div>
                            </div>
                            <div className='flex items-start gap-4'>
                                <div className='bg-mid-green/20 p-3 rounded-lg'>
                                    <Clock className='w-6 h-6 text-mid-green' />
                                </div>
                                <div>
                                    <h3 className='mb-1 font-medium text-white'>
                                        {isRTL
                                            ? 'שעות פעילות'
                                            : 'Working Hours'}
                                    </h3>
                                    <ul className='space-y-1 text-mid-grey'>
                                        {/* Sun-Thu */}
                                        <li className='flex justify-between gap-4'>
                                            <b>
                                                {isRTL ? 'א׳–ה׳:' : 'Sun–Thu:'}
                                            </b>
                                            <span>10:00 – 19:00</span>
                                        </li>

                                        {/* Friday */}
                                        <li className='flex justify-between gap-4'>
                                            <b>{isRTL ? 'ו׳:' : 'Fri:'}</b>
                                            <span>9:00 – 14:00</span>
                                        </li>

                                        {/* Saturday night */}
                                        <li className='flex justify-between gap-4'>
                                            <b>
                                                {isRTL
                                                    ? 'מוצ״ש:'
                                                    : 'Sat night:'}
                                            </b>
                                            <span>
                                                {isRTL
                                                    ? 'בתיאום מראש'
                                                    : 'By appointment'}
                                            </span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        {/* CTA Side */}
                        <div className='text-center lg:text-start'>
                            <div className='bg-mid-green/10 border border-mid-green/20 rounded-xl overflow-clip'>
                                {/* <MessageCircle className='mx-auto lg:mx-0 mb-6 w-12 h-12 text-mid-green' />
                                <h3 className='mb-4 font-display text-white text-2xl uppercase'>
                                    {isRTL
                                        ? 'הדרך המהירה להזמין תור'
                                        : 'Fastest Way to Book'}
                                </h3>
                                <p className='mb-6 text-mid-grey'>
                                    {isRTL
                                        ? 'שלח לי הודעה בוואטסאפ ונתאם יחד את הפגישה שלך'
                                        : "Send me a message on WhatsApp and let's schedule your session"}
                                </p>
                                <a
                                    href={socialLinks.whatsapp}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className='inline-flex items-center gap-3 bg-mid-green hover:bg-mid-green/90 hover:shadow-lg hover:shadow-mid-green/25 px-8 py-4 rounded-full text-black text-sm uppercase tracking-wider hover:scale-105 transition-all duration-300'
                                >
                                    <MessageCircle className='w-5 h-5' />
                                    {t.contact.whatsapp}
                                </a> */}
                                <div className='flex-1'>
                                    <MapContainer
                                        center={[
                                            (beitShemeshPosition[0] +
                                                ganYavnePosition[0]) /
                                                2,
                                            (beitShemeshPosition[1] +
                                                ganYavnePosition[1]) /
                                                2,
                                        ]}
                                        zoom={10}
                                        className='w-full h-[390px] leaflet-map'
                                    >
                                        <TileLayer
                                            url='https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png'
                                            attribution='© OpenStreetMap © CARTO'
                                        />
                                        <Marker
                                            position={beitShemeshPosition}
                                            icon={customIcon}
                                        >
                                            <Popup>סניף בית שמש </Popup>
                                        </Marker>
                                        <Marker
                                            position={ganYavnePosition}
                                            icon={customIcon}
                                        >
                                            <Popup>סניף גן יבנה</Popup>
                                        </Marker>
                                    </MapContainer>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Contact;
